<template>
    <div v-if="isReady && isVisibleCTA" :class="['cta', { 'cta-promo': hasDiscount }]">
        <template v-if="hasLabel">
            <template v-if="useNewLabelDesign">
                <dynamic-link
                    v-if="settings.labelTo"
                    :to="settings.labelTo"
                    :event="{ ...settings.labelEvent, label: settings.labelTo, content: label }"
                    native-type="a"
                >
                    <a-label
                        class="cta-label-new"
                        :variant="settings.labelVariant || 'success'"
                        :layout="settings.labelLayout || 'subtle'"
                        :type="settings.labelType || 'label'"
                        :theme="settings.labelTheme"
                        :glyph="settings.labelGlyph"
                        :size="settings.labelSize"
                    >
                        <a-glyph v-if="hasDiscount && glyph" :name="glyph" size="s" />
                        <span v-html="label" />
                    </a-label>
                </dynamic-link>
                <a-label
                    v-else
                    class="cta-label-new"
                    :variant="settings.labelVariant || 'success'"
                    :type="settings.labelType || 'label'"
                    :layout="settings.labelLayout || 'subtle'"
                    :theme="settings.labelTheme"
                    :glyph="settings.labelGlyph"
                    :size="settings.labelSize"
                >
                    <a-glyph v-if="hasDiscount && glyph" :name="glyph" size="s" />
                    <span v-html="label" />
                </a-label>
            </template>
            <template v-else>
                <dynamic-link
                    v-if="settings.labelTo"
                    :to="settings.labelTo"
                    :event="{ ...settings.labelEvent, label: settings.labelTo, content: label }"
                    native-type="a"
                >
                    <div class="cta-label">
                        <a-glyph v-if="hasDiscount && glyph" :name="glyph" />
                        <span v-html="label" />
                    </div>
                </dynamic-link>
                <div v-else class="cta-label">
                    <a-glyph v-if="hasDiscount && glyph" :name="glyph" />
                    <span v-html="label" />
                </div>
            </template>
        </template>
        <div v-if="hasDescription" class="cta-description">
            <dynamic-link v-if="settings.to" :to="settings.to" native-type="a">
                <span v-html="description" />
            </dynamic-link>
            <p v-else v-html="description" />
        </div>

        <div v-if="trialPage && hasDiscount" class="cta-trial">
            <p v-if="trialPage.label" class="cta-trial-label">
                <a-glyph v-if="trialPage.glyph" :name="trialPage.glyph" />
                <span v-html="trialPage.label" />
            </p>
            <p v-if="trialPage.description" v-html="trialPage.description" />
        </div>
    </div>
</template>

<script>
import { merge } from 'lodash-es';

import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALabel from '@core/components/label/label.vue';
import analytics from '@core/mixins/analytics.js';
import cleverbridge from '@core/mixins/cleverbridge.js';
import currency from '@core/mixins/currency.js';
import productMixin from '@core/mixins/product.js';
import purchasing from '@core/mixins/purchasing.js';

export default {
    name: 'AProductCta',

    components: {
        DynamicLink,
        AGlyph,
        ALabel,
    },

    mixins: [productMixin, purchasing, currency, analytics, cleverbridge],

    props: {
        settings: {
            type: Object,
            default: null,
        },
        useNewLabelDesign: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            label: '',
            glyph: '',
            description: '',
            hasDiscount: false,
            isReady: false,
        };
    },

    computed: {
        isVisibleCTA() {
            if (!this.isLoadedPrices) return false;
            return this.hasLabel || this.hasDescription || (this.trialPage && this.hasDiscount);
        },

        hasLabel() {
            return this.settings.hasLabel && this.label;
        },

        hasDescription() {
            return this.settings.hasDescription && this.description;
        },

        trialPage() {
            return this.settings?.trialPageCTA;
        },
    },

    async mounted() {
        await this.getCurrency();
        const productId = this.settings?.productId || this.internalProductId;
        const pageSettingsCTA = this.productItems[productId]?.parameters?.common?.productPageCTA;
        const settingsCTA = merge({}, pageSettingsCTA, this.settings);
        const cleverBridgeCartID = settingsCTA.cleverBridgeCartID;
        let license = this.licenseData({ cleverBridgeCartID, settingsCTA });

        let promo = license.promoSettings || {};

        if (!this.settings?.cleverBridgeCartID) {
            const comparison = (item) => item.product_id === productId;
            const promotion = this.promotionList.find(comparison) || {};
            if (!promo.product_page_license_id) promo = promotion.promoSettings || {};
            const licenseIdOverride = Number(promotion.license_id_override) || promo.product_page_license_id;
            if (licenseIdOverride && licenseIdOverride !== cleverBridgeCartID) {
                const overrideLicense = this.licenseData({ cleverBridgeCartID: licenseIdOverride, settingsCTA });
                if (overrideLicense.price) license = overrideLicense;
            }
        }

        this.hasDiscount = license.discount && !settingsCTA.doNotApplyPromo;
        this.glyph = this.hasDiscount && promo.glyph || settingsCTA.glyph || '';

        const period = settingsCTA.isMonthlyPrice ? 12 : 1;
        const price = this.setCurrency(license.price / period, { productId });
        const pricePromo = this.hasDiscount ? this.setCurrency(license.price_promo / period, { productId }) : '';
        const text = this.hasDiscount && promo.product_page_description || settingsCTA.description || '';

        this.description = text
            .replace(/@PricePromo/i, pricePromo)
            .replace(/@PriceFull/i, price)
            .replace(/@Price/i, price);

        if (this.settings.hasLabel) {
            const label = this.hasDiscount && promo.product_page_label;
            this.label = label || settingsCTA.promo?.label || settingsCTA.label || '';
            this.glyph = settingsCTA.promo?.glyph || settingsCTA.glyph || '';
        }

        if (this.settings.hasFullLabel) {
            this.label = `${this.label} ${this.description.charAt(0).toLowerCase()}${this.description.slice(1)}`;
        }

        this.isReady = true;
    },

    async serverPrefetch() {
        if (!import.meta.env.SSR) return;

        if (!this.settings) return;
        await this.getLicenses(this.settings);
    },
};
</script>

<style lang="postcss" scoped>
.cta {
    @mixin title;
    color: var(--av-nav-primary);
}

.cta-label {
    display: flex;
    font-weight: 400;
    align-items: center;
    color: var(--av-button-action);

    .a-glyph {
        width: 24px;
        height: 24px;
        margin-inline-end: 8px;
        fill: var(--av-fixed-success);
    }
}

.cta-description {
    display: flex;
    flex-wrap: wrap;
    margin: 8px 0 0;
    align-items: center;

    del {
        color: var(--av-fixed-light);
    }
    :deep(a) {
        b {
            color: var(--av-link-primary);
            &:hover {
                color: var(--av-link-primary-hover);
            }
            &:active {
                color: var(--av-link-primary-active);
            }
        }
    }
}

.cta-trial {
    @mixin body;
    padding-inline-start: 32px;
    color: var(--av-fixed-secondary);

    a {
        color: var(--av-brand-secondary);
    }

    .a-glyph {
        top: 0;
        inset-inline-start: -32px;
        width: 24px;
        height: 24px;
        position: absolute;
        fill: var(--av-fixed-success);
    }
}

.cta-trial-label {
    font-weight: 600;
    position: relative;
}
</style>
